import type { Initiative } from "@/lib/types";
import { PriorityBadge, StatusBadge } from "./ui/badge";
import { fmtDate } from "@/lib/quarter";

interface Props {
  initiatives: Initiative[];
  onSelect: (i: Initiative) => void;
}

export function TableView({ initiatives, onSelect }: Props) {
  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th>Title</Th>
              <Th>Team</Th>
              <Th>Product</Th>
              <Th>Status</Th>
              <Th>Priority</Th>
              <Th>Owner</Th>
              <Th>Dates</Th>
              <Th>Jira</Th>
            </tr>
          </thead>
          <tbody>
            {initiatives.map((it) => (
              <tr
                key={it.id}
                className="border-t cursor-pointer hover:bg-accent/40 transition-colors"
                onClick={() => onSelect(it)}
              >
                <td className="px-4 py-3 font-medium">{it.title}</td>
                <td className="px-4 py-3">
                  <span
                    className="inline-flex items-center gap-1.5 text-xs"
                  >
                    <span
                      className="h-2 w-2 rounded-sm"
                      style={{ backgroundColor: it.team_color }}
                    />
                    {it.team_name}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{it.product_name}</td>
                <td className="px-4 py-3">
                  <StatusBadge status={it.status} />
                </td>
                <td className="px-4 py-3">
                  <PriorityBadge priority={it.priority} />
                </td>
                <td className="px-4 py-3 text-muted-foreground">{it.owner ?? "—"}</td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                  {fmtDate(it.start_date)} → {fmtDate(it.end_date)}
                </td>
                <td className="px-4 py-3 text-muted-foreground font-mono text-xs">
                  {it.jira_key ?? "—"}
                </td>
              </tr>
            ))}
            {initiatives.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-sm text-muted-foreground">
                  No initiatives match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-3 text-left font-semibold">{children}</th>;
}
